import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import {
    TimeRangeSelector,
    ErrorAlert,
    Fields,
    VerticalFlex,
    Label,
    Switch,
    HorizontalFlex,
    FieldLabel,
    timeAndPrecisionTypes,
} from 'smartsuite-ui';
import PrecisionSelector from '../shared/filtering/PrecisionSelector';
import {
    maxPrecisionValue,
    precisionTypes,
    dateTimeFormat,
    standardPredefinedDateRanges,
} from '../shared/constants/constants';
import { useLabelContext } from '../shared/hooks/labelContext';
import {
    getIsFixedRangeInOrder,
    getIsLastNextRangeValid,
    getIsPrecisionValid,
    isRangeValueWithinMin,
    isRangeValueWithinMax,
    getRangeDomain,
    isFixedRangeWithinMin,
    isFixedRangeWithinMax,
    areTimeAndPrecisionFieldsValid,
} from '../shared/filtering/filtersModalUtils';
import {
    getSmallestPrecisionTypeAndPrecisionValue,
    validateTimeAndPrecision,
} from '../shared/filtering/timeAndPrecisionValidation';
import {
    fixedDateIsValidAndDifferent,
    rangeTypeIsDifferent,
    timeAndPrecisionTypeIsDifferent,
} from '../viewDashboards/utils/configurationAndValidationUtils';
import { timeRangeRollup } from '../shared/filtering/rollupTextUtils';

function rangeValueIsDifferent(timeAndPrecision, updatedTimeAndPrecision) {
    return timeAndPrecision.rangeValue !== updatedTimeAndPrecision.rangeValue;
}

// eslint-disable-next-line complexity
function shouldResetPrecisionValues(timeAndPrecision, updatedTimeAndPrecision) {
    return (
        timeAndPrecisionTypeIsDifferent(
            timeAndPrecision,
            updatedTimeAndPrecision
        ) ||
        rangeTypeIsDifferent(timeAndPrecision, updatedTimeAndPrecision) ||
        rangeValueIsDifferent(timeAndPrecision, updatedTimeAndPrecision) ||
        (timeAndPrecision.timeAndPrecisionType === 'fixed' &&
            fixedDateIsValidAndDifferent(
                timeAndPrecision,
                updatedTimeAndPrecision
            ))
    );
}

/* eslint-disable complexity */
export default function EditorTimeAndPrecisionSelector(props) {
    const { translationLabels, dashboardLabels, interpolateString } =
        useLabelContext();

    const handleTimeRangeChange = (updatedTimeAndPrecision) => {
        let timeAndPrecision = {
            ...updatedTimeAndPrecision,
        };

        if (
            shouldResetPrecisionValues(
                props.timeAndPrecision,
                updatedTimeAndPrecision
            )
        ) {
            const precisionResetValues =
                getSmallestPrecisionTypeAndPrecisionValue(
                    timeAndPrecision,
                    props.precisionTypeOptions
                );

            timeAndPrecision = {
                ...timeAndPrecision,
                ...precisionResetValues,
            };
        }

        if (timeAndPrecision.startDateTime) {
            if (
                timeAndPrecision.timeAndPrecisionType ===
                timeAndPrecisionTypes.fixedDay
            ) {
                timeAndPrecision.startDateTime = moment(
                    timeAndPrecision.startDateTime
                ).startOf('day');

                timeAndPrecision.endDateTime = moment(
                    timeAndPrecision.startDateTime
                )
                    .startOf('day')
                    .add(1, 'days');
            }

            timeAndPrecision.startDateTime = moment(
                timeAndPrecision.startDateTime
            ).format(dateTimeFormat);
        }

        if (timeAndPrecision.endDateTime) {
            timeAndPrecision.endDateTime = moment(
                timeAndPrecision.endDateTime
            ).format(dateTimeFormat);
        }

        if (_.has(timeAndPrecision, 'selectedDateRangeOption')) {
            timeAndPrecision = {
                ...timeAndPrecision,
                ...timeAndPrecision.selectedDateRangeOption,
            };
        }

        props.saveTimeAndPrecision(timeAndPrecision);
    };

    function renderErrorMessage() {
        const isFixedRangeInOrder = getIsFixedRangeInOrder(
            props.timeAndPrecision
        );

        if (!isFixedRangeInOrder) {
            return <ErrorAlert>{dashboardLabels.invalidFixedRange}</ErrorAlert>;
        }

        const fixedRangeDomain =
            props.timeAndPrecisionRangeDomains?.fixed ?? {};

        const fixedRangeIsWithinMax = isFixedRangeWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        const fixedRangeIsWithinMin = isFixedRangeWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        if (!fixedRangeIsWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    fixedRangeDomain.max
                } ${
                    dashboardLabels[fixedRangeDomain.precisionType]
                }`}</ErrorAlert>
            );
        }

        if (!fixedRangeIsWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${
                    fixedRangeDomain.min
                } ${
                    dashboardLabels[fixedRangeDomain.precisionType]
                }`}</ErrorAlert>
            );
        }

        const isPrecisionValid = getIsPrecisionValid(props.timeAndPrecision);
        const isLastNextRangeValid = getIsLastNextRangeValid(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        const rangeDomain = getRangeDomain(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isLastNextRangeWithinMin = isRangeValueWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );
        const isLastNextRangeWithinMax = isRangeValueWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionRangeDomains
        );

        if (!isLastNextRangeWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${
                    rangeDomain.min
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    rangeDomain.max
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (props.timeAndPrecision.precisionValue > maxPrecisionValue) {
            return (
                <ErrorAlert>{`${
                    dashboardLabels.timeRangeExceedsMax
                } ${maxPrecisionValue} ${
                    dashboardLabels[props.timeAndPrecision.precisionType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeValid || !isPrecisionValid) {
            return null;
        }

        const { isTimeAndPrecisionValid, errors } = validateTimeAndPrecision(
            props.timeAndPrecision,
            interpolateString,
            dashboardLabels,
            props.isGauge
        );

        if (isTimeAndPrecisionValid) {
            return null;
        }

        return (
            <ErrorAlert>
                <ul>
                    {errors.map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </ul>
            </ErrorAlert>
        );
    }

    if (!props.showTimeAndPrecision) {
        return null;
    }

    let timeAndPrecision = { ...props.timeAndPrecision };
    if (timeAndPrecision.startDateTime) {
        timeAndPrecision = {
            ...timeAndPrecision,
            startDateTime: moment(timeAndPrecision.startDateTime).toDate(),
        };
    }
    if (timeAndPrecision.endDateTime) {
        timeAndPrecision = {
            ...timeAndPrecision,
            endDateTime: moment(timeAndPrecision.endDateTime).toDate(),
        };
    }

    function renderDashboardTimeRangeText() {
        const invalidDashboardTimeAndPrecision =
            !areTimeAndPrecisionFieldsValid(
                props.dashboardTimeAndPrecision,
                {},
                true,
                interpolateString,
                dashboardLabels
            );

        if (invalidDashboardTimeAndPrecision) {
            return `(${dashboardLabels.invalid})`;
        }

        return `(${timeRangeRollup(
            props.dashboardTimeAndPrecision,
            dashboardLabels
        )})`;
    }

    const timeRangeClassName = props.useDashboardTimeRange
        ? 'editor-drawer__time-range-selector editor-drawer__time-range-selector--hidden'
        : 'editor-drawer__time-range-selector';

    return (
        <div>
            <HorizontalFlex>
                <Switch
                    checked={props.useDashboardTimeRange}
                    onChange={(event) => {
                        props.setUseDashboardTimeRange(event.target.checked);
                    }}
                />
                <VerticalFlex>
                    <Label>
                        {props.useDashboardTimeRange
                            ? dashboardLabels.usingDashboardTimeRange
                            : dashboardLabels.useWidgetTimeRange}
                    </Label>
                    {props.useDashboardTimeRange && (
                        <FieldLabel>
                            {renderDashboardTimeRangeText()}
                        </FieldLabel>
                    )}
                </VerticalFlex>
            </HorizontalFlex>
            {renderErrorMessage()}
            <div className={timeRangeClassName}>
                <VerticalFlex>
                    <Fields
                        cssClassModifiers={[
                            'no-background',
                            'no-pad-top-bottom',
                            'column',
                        ]}
                    >
                        <TimeRangeSelector
                            labels={{
                                ...translationLabels,
                                ...dashboardLabels,
                            }}
                            precisionTypeOptions={_.values(precisionTypes)}
                            predefinedDateRangeOptions={
                                standardPredefinedDateRanges
                            }
                            predicateLabel={dashboardLabels.timeRange}
                            saveTimeAndPrecision={handleTimeRangeChange}
                            timeAndPrecision={timeAndPrecision}
                        />
                    </Fields>
                    <PrecisionSelector
                        precisionTypeOptions={
                            props.precisionTypeOptions?.[
                                timeAndPrecision.rangeType
                            ] ?? _.values(precisionTypes)
                        }
                        saveTimeAndPrecision={handleTimeRangeChange}
                        showPrecisionConfig={props.showPrecisionConfig}
                        timeAndPrecision={timeAndPrecision}
                        isGauge={props.isGauge}
                    />
                </VerticalFlex>
            </div>
        </div>
    );
}
/* eslint-enable complexity */

EditorTimeAndPrecisionSelector.propTypes = {
    dashboardTimeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    isGauge: PropTypes.bool.isRequired,
    precisionTypeOptions: PropTypes.instanceOf(Object),
    saveTimeAndPrecision: PropTypes.func.isRequired,
    setUseDashboardTimeRange: PropTypes.func.isRequired,
    showPrecisionConfig: PropTypes.bool.isRequired,
    showTimeAndPrecision: PropTypes.bool.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    timeAndPrecisionRangeDomains: PropTypes.instanceOf(Object),
    useDashboardTimeRange: PropTypes.bool,
};

EditorTimeAndPrecisionSelector.defaultProps = {
    useDashboardTimeRange: false,
};
